import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import userService from '../services/userService';
import tradeService from '../services/tradeService';
import insertSetService from '../services/insertSetService';
import slugService from '../services/slugService';
import CardItem from '../components/CardItem';
import PageGrid from '../components/PageGrid';
import Sidebar from '../components/Sidebar';
import SidebarSection from '../components/Sidebar/SidebarSection';
import SidebarRow from '../components/Sidebar/SidebarRow';
import SidebarImage from '../components/Sidebar/SidebarImage';
import UserContactCard from '../components/UserContactCard';
import LoadingSpinner from '../components/LoadingSpinner';
import usePageTitle from '../utils/usePageTitle';
import { getUserAvailableCardsInsertSetBreadcrumbs } from '../utils/breadcrumbUtils';
import { filterCards } from '../utils/filters';

/**
 * User Available Cards Insert Set Page - displays cards a user has marked as available
 * from a specific insert set
 */
const UserAvailableCardsInsertSetPage = ({ username, productSlug, insertSetSlug }) => {
  const [userData, setUserData] = useState(null);
  const [productData, setProductData] = useState(null);
  const [insertSetData, setInsertSetData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Set page title
  usePageTitle(
    userData && productData && insertSetData
      ? `${userData.name}'s Available ${insertSetData.name} Cards` 
      : 'Available Cards'
  );
  
  // Load user, product, and insert set data when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load user data
        const user = await userService.getUserByUsername(username);
        setUserData(user);
        
        // Load insert set data from slug
        const insertSet = await slugService.getInsertSetBySlug(productSlug, insertSetSlug);
        setInsertSetData(insertSet);
        
        // Load product data from slug
        const productData = await insertSetService.getProductForInsertSet(insertSet);
        setProductData(productData);
  } catch (error) {
        console.error('Error loading user available cards insert set data:', error);
        setError(error.message || 'Failed to load available cards');
      } finally {
        setLoading(false);
      }
    };
    
    if (username && productSlug && insertSetSlug) {
      loadData();
    }
  }, [username, productSlug, insertSetSlug]);
  
  // Function to fetch cards for this insert set that the user has available
  const fetchCards = useCallback(async (page, limit) => {
    try {
      if (!userData || !userData.id || !insertSetData || !insertSetData.id) {
        throw new Error('User data and insert set data are required');
      }
      
      const response = await tradeService.getUserCardsForTradeByInsertSet(
        userData.id, 
        insertSetData.id, 
        page, 
        limit
      );
      
      return {
        items: response.cardsForTrade.map(item => ({
          ...item.card,
          inForTrade: true,
          cardForTradeId: item._id
        })),
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalItems: response.totalItems
      };
    } catch (error) {
      console.error('Error fetching available cards by insert set:', error);
      throw error;
    }
  }, [userData, insertSetData]);
  
  
  // Function to render each card item
  const renderCardItem = useCallback((card) => {
    if (!userData || !productData || !insertSetData) return null;
    
    return (
      <CardItem 
        card={card}
        insertSet={insertSetData}
        product={productData}
        cardUrl={slugService.getUserAvailableCardsCardUrl(card, insertSetData, productData, username)}
        showInventoryControls={userData.isOwnProfile}
        showWishlistToggle={userData.isOwnProfile}
        showTradeToggle={userData.isOwnProfile}
      />
    );
  }, [userData, productData, insertSetData, username]);
  
  // Render the sidebar content
  const renderSidebar = useCallback(() => {
    if (!userData || !productData || !insertSetData) {
      return null;
    }
    
    return (
      <Sidebar title={insertSetData.name}>
        {insertSetData.imageUrl && (
          <SidebarImage 
            src={insertSetData.imageUrl} 
            alt={insertSetData.name} 
          />
        )}
        
        <SidebarSection title="Insert Set Information">
          <SidebarRow
            label="Product"
            value={`${productData.name} (${productData.year})`}
          />
          {insertSetData.description && (
            <SidebarRow
              label="Description"
              value={insertSetData.description}
              multiline
            />
          )}
        </SidebarSection>
        
        {/* User Contact Information Card */}
        <UserContactCard user={userData} />
      </Sidebar>
    );
  }, [userData, productData, insertSetData]);
  
  // If no required params are provided, redirect to home
  if (!username || !productSlug || !insertSetSlug) {
    return <Navigate to="/" replace />;
  }
  
  // Show loading spinner while data is being loaded
  if (loading) {
    return <LoadingSpinner />;
  }
  
  // Show error message if there was an error loading the data
  if (error) {
    return (
      <div className="error-container">
        <h2>Error Loading Available Cards</h2>
        <p>{error}</p>
      </div>
    );
  }
  
  if(!userData || !productData || !insertSetData) {
    return <></>;
  }
  
  // Generate breadcrumb items
  const breadcrumbItems = getUserAvailableCardsInsertSetBreadcrumbs(userData, productData, insertSetData);

  return (
    <PageGrid
      title={`${userData.name}'s Available ${insertSetData.name} Cards`}
      fetchItems={fetchCards}
      filterFunction={filterCards}
      renderItem={renderCardItem}
      renderSidebar={renderSidebar}
      placeholderText={`Filter cards...`}
      emptyStateMessage={`No available cards found for ${insertSetData.name}`}
      itemsPerPage={30}
      backButton={{
        url: `/@${username}/available-cards/${productSlug}`,
        label: "Back to Insert Sets"
      }}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default UserAvailableCardsInsertSetPage;